import { Component, OnInit } from '@angular/core';
import { ButtonStyle, NotificationService } from 'rcp-common-lib';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import {
    AccountExternalSystemResponse,
    ClaimFileResponse,
} from '@core/generated/openapi';
import { ClaimService } from '@services/claim/claim.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AccountExternalSystemService } from '@services/account-external-system/account-external-system.service';
import {
    AdminRoutes,
    ModuleRoutes,
} from '@core/constants/module-history.constants';
import { AuthService } from '@services/auth/auth.service';
import { isNil } from 'lodash-es';
import { PM_SYSTEM_IDS } from '@core/constants/external-system.constants';

@UntilDestroy()
@Component({
    selector: 'rcp-oup-aging-latest-upload-summary',
    templateUrl: './aging-latest-upload-summary.component.html',
    styleUrls: ['./aging-latest-upload-summary.component.scss'],
    standalone: false,
})
export class AgingLatestUploadSummaryComponent implements OnInit {
    isLoading: boolean = false;
    latestFile: ClaimFileResponse;
    previousFile: ClaimFileResponse;
    agingFiles: ClaimFileResponse[];
    pmSystem: AccountExternalSystemResponse;

    selectedLocations: number[];

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private claimService: ClaimService,
        private notificationService: NotificationService,
        private externalSystemService: AccountExternalSystemService,
        private authService: AuthService,
    ) {}

    ngOnInit(): void {
        this.authService.agingLocationFilter
            .pipe(untilDestroyed(this))
            .subscribe((locations) => {
                if (this.authService.isAgingUploadByLocationOff(locations)) {
                    this.selectedLocations = undefined;
                    this.lookup();
                } else if (!isNil(locations)) {
                    this.selectedLocations = locations;
                    this.lookup();
                }
            });
    }

    lookup() {
        this.isLoading = true;

        this.claimService
            .findAgingFileUploadHistory(this.selectedLocations)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (result) => {
                    const nonDeletedFiles = result.filter(
                        (file) =>
                            file.status !==
                            ClaimFileResponse.StatusEnum.Deleted,
                    );
                    nonDeletedFiles.sort(
                        (a, b) =>
                            new Date(b.createdOn).getTime() -
                            new Date(a.createdOn).getTime(),
                    );
                    this.agingFiles = nonDeletedFiles;

                    if (this.agingFiles.length > 1) {
                        this.latestFile = this.agingFiles[0];
                        this.previousFile = this.agingFiles[1];
                    } else if (this.agingFiles.length == 1) {
                        this.latestFile = this.agingFiles[0];
                        this.previousFile = undefined;
                    } else {
                        this.latestFile = undefined;
                        this.previousFile = undefined;
                    }
                },
                error: (error) => {
                    this.isLoading = false;
                    this.notificationService.showError(
                        'Error loading latest Aging Files.',
                        error.error?.message,
                    );
                    console.error(error);
                    this.isLoading = false;
                },
                complete: () => {
                    this.isLoading = false;
                },
            });

        this.externalSystemService
            .findAll()
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (results) => {
                    this.pmSystem = results.find((system) => {
                        return (
                            system.type ===
                            AccountExternalSystemResponse.TypeEnum.PmSystem
                        );
                    });
                    this.isLoading = false;
                },
                error: (error) => {
                    this.isLoading = false;
                    this.notificationService.showError(
                        'Error looking up PM Software.',
                        error.error?.message,
                    );
                },
                complete: () => {
                    this.isLoading = false;
                },
            });
    }

    calculateSummary(
        agingFile: ClaimFileResponse,
        status: string,
    ): { count: number; balance: number } {
        if (agingFile) {
            const summary = agingFile.agingFileSummaries?.find(
                (s) => s.status === status,
            );
            return {
                count: summary?.count || 0,
                balance: summary?.balance || 0,
            };
        } else {
            return {
                count: 0,
                balance: 0,
            };
        }
    }

    getChangeValue(record: ClaimFileResponse) {
        const agingFileSummaries = record.agingFileSummaries;
        const newBalance =
            agingFileSummaries?.find((summary) => summary.status === 'NEW')
                ?.balance || 0;
        const closedBalance =
            agingFileSummaries?.find((summary) => summary.status === 'CLOSED')
                ?.balance || 0;

        return newBalance - closedBalance;
    }

    goToFileDetails(record: ClaimFileResponse) {
        const id = record.id;
        this.router.navigate(['../uploadClaims/history/' + record.id], {
            relativeTo: this.route,
        });
    }

    goToUploadFile() {
        this.router.navigate(['../uploadClaims'], { relativeTo: this.route });
        this.claimService.openUploadClaimFileDialog(
            this.pmSystem?.masterExternalSystem,
        );
    }

    goToPmSoftware() {
        this.router.navigateByUrl(
            `/${ModuleRoutes.Admin}/${AdminRoutes.Externals}/pmSoftware`,
        );
    }

    protected readonly ButtonStyle = ButtonStyle;
    protected readonly PM_SYSTEM_IDS = PM_SYSTEM_IDS;
}
